import { BASE_TILE_TEXTURE_SPAN, TILE_KIND_DETAIL } from '../GameWorld/tiles';

const ART_DIRECTION = `ART DIRECTION:
Original smooth cel-shaded post-apocalyptic comic style for the game “Progress Lands”.
The world should look improvised, repaired, weathered, and built from scavenged materials.
Use readable shapes, restrained hand-painted variation, clean silhouettes, and a limited dusty palette.
Add restrained scratches, cracks, rust, debris, chipped paint, or environmental wear where appropriate without making the surface noisy.
Avoid glossy fantasy, clean futuristic materials, photorealism, photographic noise, and text.`;

const SHADER_READY_STYLE = `SHADER-READY STYLE:
- smooth anti-aliased cel-shaded artwork with subtle painterly surface variation
- broad low-frequency color and material shapes that remain readable after runtime blending
- no visible square pixels, pixel-art dithering, or nearest-neighbor stair steps
- avoid dense high-frequency noise that would shimmer during camera movement
- no watermark, frame, labels, logo, UI, mockup, or presentation sheet`;

const BASE_TILE_REQUIREMENTS = `TILE REQUIREMENTS:
- strict top-down orthographic terrain view
- create one continuous terrain texture patch spanning approximately ${BASE_TILE_TEXTURE_SPAN} × ${BASE_TILE_TEXTURE_SPAN} gameplay tiles, not artwork for a single gameplay tile
- true seamless tile on all four edges
- left edge must match right edge; top edge must match bottom edge
- no visible border, frame, grid line, edge shadow, central focal object, or lighting seam
- use broad irregular color and material variation across the full patch with subtle sparse micro-detail
- no dominant diagonal, horizontal, or vertical pattern; no evenly spaced ripples, stripes, dots, checkerboard, or recurring motif
- depict only the named terrain material; category- and tag-driven transitions, water motion, and height cues are added by the renderer
- no baked biome border, shoreline transition, cliff face, height step, raised rim, or directional cast shadow
- reserve recognizable rocks, debris, plants, shells, and isolated cracks for transparent detail overlays
- fill the full canvas edge to edge
- fully opaque; no alpha transparency`;

const DETAIL_TILE_REQUIREMENTS = `TILE REQUIREMENTS:
- strict top-down orthographic map decoration or terrain detail
- one clearly readable detail cluster centered in the canvas
- scale the subject to fill approximately 75–85% of the canvas while keeping a narrow transparent safety margin
- real alpha transparency outside the artwork
- keep artwork interiors fully opaque (alpha 255); partial alpha is permitted only in a narrow 1–2 pixel antialias fringe along the outer silhouette
- no baked terrain background, border, frame, grid line, or edge shadow
- do not crop the detail at the canvas edge`;

export function buildTileImagePrompt(tile = {}) {
  const isDetail = tile.type === TILE_KIND_DETAIL;
  const name = tile.name?.trim() || '[ENTER TILE NAME]';
  const description = tile.visualDescription?.trim() || '[ENTER VISUAL DESCRIPTION]';
  const role = isDetail ? 'Transparent detail overlay' : 'World-aligned seamless opaque base terrain texture';
  const requirements = isDetail ? DETAIL_TILE_REQUIREMENTS : BASE_TILE_REQUIREMENTS;
  const output = isDetail
    ? `OUTPUT:
- square 1:1 aspect ratio
- 256 × 256 pixels
- transparent PNG or lossless WebP with alpha
- standalone production-ready map overlay
- example asset path: regions/amnesia-cove/detail-driftwood-a.png`
    : `OUTPUT:
- Seamless tile PNG/WebP
- square 1:1 aspect ratio
- exactly 256 × 256 pixels
- fully opaque
- standalone production-ready map terrain asset
- example asset path: regions/amnesia-cove/tile-sand-a.webp`;

  return `Create a single production-ready smooth cel-shaded map asset for the game “Progress Lands”.

TILE NAME:
${name}

VISUAL DESCRIPTION:
${description}

TILE ROLE:
${role}

${ART_DIRECTION}

${SHADER_READY_STYLE}

${requirements}

COLOR AND LIGHTING:
- use colors appropriate to the visual description while staying within the Progress Lands palette
- keep values readable beneath player markers, paths, and selection overlays
- keep base terrain evenly lit from a near-overhead source so adjacent materials can blend without a lighting seam
- for transparent details, use restrained upper-left cel-shaded highlights and controlled lower-right shading
- do not prepaint runtime terrain transitions, animated water highlights, cliff rims, or cliff faces
- no ambient glow or realistic reflections

${output}`;
}
